import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from "react-redux";
import { getPreferences, getAccountValues, setAccountValue } from '../store/user/thunks';
import { getTradesStats, getTradesStatsFiltered, getTradesPage } from '../store/trades/thunks';
import {
  getPlanAdherence,
  getRiskRewardStats,
  getAccountValueMetrics,
  getJournalStreak,
} from '../store/analytics/thunks';
import { createTradeReview, uploadReviewScreenshot } from '../store/journal/thunks';
import { searchTicker, reset, updateTrades } from '../store/trade'
import { getWatchlist } from '../store/study/thunks'
import { useNavigate, useLocation } from "react-router-dom";
import { IoFilter } from "react-icons/io5";
import { returnInTZ } from '../utils/timezone';
import '../styles/filter.css';
import '../styles/home.css';
import '../styles/profile.css';
import Lottie from "lottie-react";
import animationData from "../lotties/no-data-animation.json";
import { TriangleDownIcon, TriangleUpIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import getSymbolFromCurrency from 'currency-symbol-map';
import {
  Flex,
  Text,
  Center,
  Heading,
  Input,
  Checkbox,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Stack,
  Textarea,
  Select,
  useColorMode,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Box,
  FormControl,
  FormHelperText,
  useDisclosure,
  HStack,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import HomeSkeleton from "./HomeSkeleton";
import TradingSpinner from "./TradingSpinner";


import useToastNotification from "../hooks/useToastNotification";
import useAsyncState from "../hooks/useAsyncState";
import usePagination from "../hooks/usePagination";
import AccountValueSyncToggle from "./common/AccountValueSyncToggle";
import HeroStatsRow from "./home/HeroStatsRow";
import DetailedStatsGrid from "./home/DetailedStatsGrid";
import TradeBreakdownCharts from "./home/TradeBreakdownCharts";
import AccountValueChartCard from "./home/AccountValueChartCard";
import AccountValueOptInDialog from "./home/AccountValueOptInDialog";
import HomeFilters from "./home/HomeFilters";
import HomeWatchlistCard from "./home/HomeWatchlistCard";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function Home({ user }) {
  const { onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const navigate = useNavigate();
  const showToast = useToastNotification();
  const dispatch = useDispatch();
  const query = useQuery();
  const { trade } = useSelector((state) => state.trade);
  const tradeSuccess = useSelector((state) => state.trade.success);
  const { trades } = useSelector((state) => state.trades);
  const { stats } = useSelector((state) => state.trades);
  const { preferences } = useSelector((state) => state.user);
  const { accountValues } = useSelector((state) => state.user);
  // TRADE-959: new headline metrics threaded into hero + detailed grid + perf card.
  const planAdherence = useSelector((state) => state.analytics.planAdherence);
  const riskReward = useSelector((state) => state.analytics.riskReward);
  const accountValueMetrics = useSelector((state) => state.analytics.accountValueMetrics);
  const journalStreak = useSelector((state) => state.analytics.journalStreak);
  useAsyncState({
    onSuccess: (info) => {
      if (info && info.result === "Account Value Set Successfully") {
        showToast(info.result);
        setOptInAlertDialog(false);
        onClose();
        dispatch(getPreferences());
        dispatch(getAccountValues({ filters: { date: today } }));
      }
    },
    onError: (info) => showToast(info?.response?.data?.result, { status: "error" }),
  });
  const hasStats = ((stats && stats.stats && Object.keys(stats.stats).length > 0 && stats.stats.num_trades > 0) ? (true):(false)); //need to look into this for home error
  const hasPreferences = ((preferences && Object.keys(preferences).length > 0) ? (true):(false)); //need to look into this for home error
  const hasAVs = ((accountValues && accountValues.accountvalues && Object.keys(accountValues.accountvalues).length > 0) ? (true):(false)); //need to look into this for home error
  const hasTrades = ((trades && trades.trades && Object.keys(trades.trades).length >= 0) ? (true):(false));

  // "Loaded" = the slice has been populated by its API call at least once,
  // independent of whether the user has any trades. Used to keep the skeleton
  // up through the full multi-thunk hydration on first login — state.ui.loading
  // alone isn't sufficient because it flips to false as soon as the *first*
  // concurrent thunk fulfills, leaving the empty-state branch to render briefly.
  const statsLoaded = stats && Object.keys(stats).length > 0;
  const preferencesLoaded = preferences && Object.keys(preferences).length > 0;
  const accountValuesLoaded = accountValues && Object.keys(accountValues).length > 0;
  const tradesLoaded = trades && Object.keys(trades).length > 0;
  const initialDataLoaded = statsLoaded && preferencesLoaded && accountValuesLoaded && tradesLoaded;


  const isWideChart = useBreakpointValue({ base: false, sm: true });
  const [toggleFilter, setToggleFilter] = useState(false);

  const [filters, setFilters] = useState(false);

  const [featureFlag, setFeatureFlag] = useState(false);
  const [optInAlertDialog, setOptInAlertDialog] = useState(false);
  const [accountvalue, setAccountvalue] = useState("0");

  // Function to format a value with the currency symbol
  const format = (val, currencyCode) => {
    const currencySymbol = getSymbolFromCurrency(currencyCode);
    return currencySymbol + val;
  };

  // Function to parse a value and remove the currency symbol
  const parse = (val, currencyCode) => {
    const currencySymbol = getSymbolFromCurrency(currencyCode);
    const regex = new RegExp(`^\\${currencySymbol}`);
    return val.replace(regex, '');
  };


  const handleFilterChange = (newFilter) => {
    query.set('filter', newFilter);
    navigate(`?${query.toString()}`);
  };

  function filtersToQueryString(filters) {
    const params = new URLSearchParams();
    for (const key in filters) {
      if (filters[key] !== '') {
        params.append(key, filters[key]);
      }
    }
    return params.toString();
  }

  useEffect(() => {
    // TRADE-959: lazy-fetch the four new headline metrics on mount.
    dispatch(getPlanAdherence());
    dispatch(getRiskRewardStats());
    dispatch(getAccountValueMetrics({ period: 'all' }));
    dispatch(getJournalStreak());
    // TRADE-973: hydrate the watchlist for HomeWatchlistCard. Dispatched here
    // (not in the card itself) because Home wraps its content tree in
    // AnimatePresence keyed on ui.loading — children inside that tree remount
    // on every loading transition, which would re-fire the fetch.
    dispatch(getWatchlist());
  }, [dispatch]);

  useEffect(() => {
    const savedHomeFilters = window.localStorage.getItem('HomeFilters');
    if (savedHomeFilters) {
      const HomeFilters = JSON.parse(savedHomeFilters);
      setFilterTradeType(HomeFilters.trade_type || "");
      setFilterSecurityType(HomeFilters.security_type || "");
      setFilterTradeDirection(HomeFilters.trade_direction || "");
      setFilterTickerName(HomeFilters.ticker_name || "");
      setSelectedTickerValue(HomeFilters.ticker_name || "");
      setFilterSwitchDate(HomeFilters.date_range || "");
      setFilterFromDate(HomeFilters.from_date || "");
      setFilterToDate(HomeFilters.to_date || "");
      setFilters(HomeFilters);
    }
  }, []); // Empty dependency array means this runs once on mount  

  const today = returnInTZ(new Date().toISOString());
  const [todayAccountValue, setTodayAccountValue] = useState(0);
  const [filter_av_time, setFilterAvDate] = useState("");
  const [avtimeloading, setAvTimeLoading] = useState(false);

  useEffect(() => {
    evaluateAvTime();
  }, [filter_av_time]); 

  const evaluateAvTime = async () => {
    if(filter_av_time !== ''){
      setAvTimeLoading(true);
      const filters = {};
      filters.date = today
      filters.time_frame = filter_av_time;
      await dispatch(getAccountValues({ filters }));
      setAvTimeLoading(false);
    }
  }
  
  const setDateFormat = () => {
    let dateFormat = {
      timeZone: "UTC",
      month: "2-digit",
      day: "2-digit",
    };
    if (filter_av_time === "Month") {
      // Format date differently for Month
      dateFormat = {
        timeZone: "UTC",
        month: "short",
        year: "numeric",
      };
    } else if (filter_av_time === "Year") {
      // Format date differently for Year
      dateFormat = {
        timeZone: "UTC",
        year: "numeric",
      };
    } else {
      // Default format for Day
      dateFormat = {
        timeZone: "UTC",
        month: "2-digit",
        day: "2-digit",
      };
    }
    return dateFormat
  }

  const [accountValueUpdateTradesEnable, setAccountValueUpdateTradesEnable] = useState(true);

  // Toggle the checkbox when the user clicks
  const handleCheckboxChange = (e,type) => {
    if (type === "UpdateTrades"){
      setAccountValueUpdateTradesEnable(e.target.checked);  // Update the state based on the toggle
    }
  };

  useEffect(() => {
  if (hasAVs && featureFlag) {
    const value = accountValues.accountvalues.find((item) => item.date === today)?.accountvalue || 0;
    setTodayAccountValue(value);      
    setAccountvalue(value);           
  }
}, [accountValues, hasAVs, featureFlag, today]);


  const handleOptInButton = (e) => {
    e.preventDefault();
    setOptInAlertDialog(true);
  };

  const handleConfirmOptIn = async (e) => {
    e.preventDefault();
    const date = today;
    await dispatch(
      setAccountValue({
        accountvalue,
        date
      })
    );
    setFilterAvDate("");
    featureFlag ? setAccountvalue(todayAccountValue) : setAccountvalue("0");
  };

  const handleCancelOptIn = (e) => {
    setOptInAlertDialog(false);
    featureFlag ? setAccountvalue(todayAccountValue) : setAccountvalue("0");
    onClose();
  };

  useEffect(() => {
    evaluateTradeSuccess();
  }, [tradeSuccess]);

  const evaluateTradeSuccess = async () => {
    if(tradeSuccess === true && trade && trade.result && trade.result.includes("Trades Updated Successfully")){
      showToast(trade.result);
      let filters = {};
      if(filter_trade_type !== ''){
        filters.trade_type = filter_trade_type;
      }
      if(filter_security_type !== ''){
        filters.security_type = filter_security_type;
      }
      if(filter_trade_direction !== ''){
        filters.trade_direction = filter_trade_direction;
      }
      if(filter_ticker_name !== ''){
        filters.ticker_name = filter_ticker_name;
      }
      if(filter_switch_time !== '' && (filter_from_date === '' && filter_to_date === '')){
        filters.date_range = filter_switch_time;
      }
      if(filter_switch_time === '' && (filter_from_date !== '' || filter_to_date !== '')){
        if (filter_from_date !== '' && filter_to_date !== ''){
          filters.from_date = filter_from_date;
          filters.to_date = filter_to_date;
        }else if (filter_from_date !== '' && filter_to_date === ''){
          filters.from_date = filter_from_date;
          filters.to_date = today;
        }
        else if (filter_from_date === '' && filter_to_date !== ''){
          filters.from_date = "1900-01-01";
          filters.to_date = filter_to_date;
        }
      }
      await dispatch(
        getTradesStatsFiltered({
          filters
        })
      );
      handleFilterChange(filtersToQueryString(filters));
      window.localStorage.setItem('HomeFilters', JSON.stringify(filters));
      filters.page = 1;
      filters.numrows = num_results;
      filters.trade_date = 'NULL';
      await dispatch(getTradesPage({ filters }));
      dispatch(
        reset()      
      );
      filters = {};
      filters.date = today
      await dispatch(getAccountValues({ filters }));
      clearFormStates();
      setSelectedRow([]);
      setTradeID(null);
    }
  }
  const [, setTradeID] = useState(null);

  const [filter_trade_type, setFilterTradeType] = useState("");
  const [filter_security_type, setFilterSecurityType] = useState("");
  const [filter_trade_direction, setFilterTradeDirection] = useState("");
  const [filter_ticker_name, setFilterTickerName] = useState("");
  const [filter_switch_time, setFilterSwitchDate] = useState("");
  const [filter_from_date, setFilterFromDate] = useState("");
  const [filter_to_date, setFilterToDate] = useState("");
  const [isFromToDisabled, setIsFromToDisabled] = useState(false);
  const [isDateRangeDisabled, setIsDateRangeDisabled] = useState(false);

  // Your logic to enable or disable the input
  useEffect(() => {
    if (filter_switch_time !== '' && (filter_from_date === '' && filter_to_date === '')) {
      setIsFromToDisabled(true);
      setIsDateRangeDisabled(false);
    } else if ((filter_switch_time === '' && (filter_from_date !== '' || filter_to_date !== ''))){
      setIsFromToDisabled(false);
      setIsDateRangeDisabled(true);
    } else if ((filter_switch_time === '' && filter_from_date === '' && filter_to_date === '')){
      setIsFromToDisabled(false);
      setIsDateRangeDisabled(false);
    }
  }, [filter_switch_time,filter_from_date,filter_to_date]);


  const authLoading = useSelector((state) => state.ui.loading);


  const { colorMode } = useColorMode();

  const [, setSearchValue] = useState('');
  const [searchTickerValue, setSearchTickerValue] = useState('');
  const [searchTickerResults, setSearchTickerResults] = useState([]);
  const [, setSelectedValue] = useState('');
  const [selectedTickerValue, setSelectedTickerValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);


  useEffect(() => {
    const fetchSearchResults = async () => {
      setIsLoading(true);
      const filter = {};
      if(searchTickerValue !== ''){
        filter.ticker_name = searchTickerValue;
      }
      const response = await dispatch(searchTicker({filter})); 
      const topResults = response.payload.tickers.map(f => [f.ticker_name]);
      setSearchTickerResults(topResults);
      setIsLoading(false);
    };

    if (searchTickerValue) {
      fetchSearchResults();
      setIsDropdownOpen(true);
    } else {
      setSearchTickerResults([]);
      setIsDropdownOpen(false);
    }
  }, [searchTickerValue]);

  const handleInputTickerFilterChange = (event) => {
    setSelectedTickerValue('');
    setSearchTickerValue(event.target.value);
    setFilterTickerName(event.target.value);
  };

  const handleInputTickerFIlterClick = (event) => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleTickerSelection = (selection) => {
    const selectionString = selection[0];
    setSelectedTickerValue(selectionString);
    setFilterTickerName(selectionString);
    setIsDropdownOpen(false);
  };

  const searchTickerResultItems = searchTickerResults.map((result) => (
    <li key={result} onClick={() => handleTickerSelection(result)}> 
      {result}
    </li>
  ));

  const keysToSkip = ['page', 'numrows', 'trade_date'];
  
  const filterLabelMap = {
    trade_type: 'Trade Type',
    security_type: 'Security Type',
    trade_direction: 'Direction',
    ticker_name: 'Ticker',
    date_range: 'Time Frame',
    from_date: 'From',
    to_date: 'To',
  };

  const handleRemoveFilter = async (key) => {
    const updated = { ...filters };
    delete updated[key];
    // If removing one date field, remove the other too
    if (key === 'from_date' || key === 'to_date') {
      delete updated['from_date'];
      delete updated['to_date'];
      setFilterFromDate('');
      setFilterToDate('');
    }
    // Reset the corresponding state
    if (key === 'trade_type') setFilterTradeType('');
    if (key === 'security_type') setFilterSecurityType('');
    if (key === 'trade_direction') setFilterTradeDirection('');
    if (key === 'ticker_name') { setFilterTickerName(''); setSearchTickerValue(''); setSelectedTickerValue(''); }
    if (key === 'date_range') setFilterSwitchDate('');

    if (Object.keys(updated).filter(k => !keysToSkip.includes(k)).length === 0) {
      // No filters left — do a full clear
      await dispatch(getTradesStats());
      handleFilterChange(filtersToQueryString({}));
      window.localStorage.removeItem('HomeFilters');
      const paginationFilters = { page: 1, numrows: 5, trade_date: 'NULL' };
      await dispatch(getTradesPage({ filters: paginationFilters }));
      setFilters({});
    } else {
      await dispatch(getTradesStatsFiltered({ filters: updated }));
      handleFilterChange(filtersToQueryString(updated));
      window.localStorage.setItem('HomeFilters', JSON.stringify(updated));
      const paginationFilters = { ...updated, page: 1, numrows: 5, trade_date: 'NULL' };
      await dispatch(getTradesPage({ filters: paginationFilters }));
      setFilters(updated);
    }
    setSelectedRow([]);
  };



  const formatter = (currencyCode) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    });
  };

  var percent = new Intl.NumberFormat('default', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const makePieOptions = (sliceColors) => ({
    chartArea: { width: '90%', height: '90%' },
    height: '100%',
    width: '100%',
    backgroundColor: 'transparent',
    pieHole: 0.45,
    is3D: false,
    pieSliceTextStyle: { color: colorMode === 'light' ? '#1A202C' : '#F7FAFC', fontName: 'Open Sans', fontSize: 12 },
    slices: sliceColors.map(c => ({ color: c })),
    legend: { position: 'none' },
    tooltip: { textStyle: { fontName: 'Open Sans' } },
  });

  const makeAreaOptions = () => ({
    chartArea: isWideChart ? { left: 60, right: 30, top: 30, bottom: 40 } : { left: 40, right: 8, top: 30, bottom: 40 },
    legend: 'none',
    curveType: 'function',
    backgroundColor: 'transparent',
    areaOpacity: 0.15,
    series: colorMode === 'dark' ? { 0: { color: '#90CAF9' } } : { 0: { color: '#3182CE' } },
    crosshair: { trigger: 'both', color: colorMode === 'light' ? '#A0AEC0' : '#718096' },
    tooltip: { textStyle: { fontName: 'Open Sans' } },
    hAxis: { textStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0', fontName: 'Open Sans', fontSize: 11 }, gridlines: { color: 'none' } },
    vAxis: { minValue: 0, textStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0', fontName: 'Open Sans', fontSize: 11 }, format: 'decimal', gridlines: { color: colorMode === 'light' ? '#EDF2F7' : '#2D3748' } },
  });

  const colorChange = (pnl) => {
    let bgColor;
    if (pnl > 0){
      bgColor = "green.400"
    } else if (pnl < 0) {
      bgColor = "red.400"
    } else {
    }
    return bgColor;
  };

  const pnlValue = (pnl) => {
    if (pnl.includes("-")){
      return "(".concat(pnl.substring(1),")")
    } else {
      return pnl
    }
  };

  const getFilterComponent = () => (
    <HomeFilters
      filters={filters}
      onRemoveFilter={handleRemoveFilter}
      labelMap={filterLabelMap}
      keysToSkip={keysToSkip}
      toggleFilter={toggleFilter}
      onToggleFilter={() => setToggleFilter(!toggleFilter)}
      filter_trade_type={filter_trade_type}
      setFilterTradeType={setFilterTradeType}
      filter_security_type={filter_security_type}
      setFilterSecurityType={setFilterSecurityType}
      filter_trade_direction={filter_trade_direction}
      setFilterTradeDirection={setFilterTradeDirection}
      filter_switch_time={filter_switch_time}
      setFilterSwitchDate={setFilterSwitchDate}
      filter_from_date={filter_from_date}
      setFilterFromDate={setFilterFromDate}
      filter_to_date={filter_to_date}
      setFilterToDate={setFilterToDate}
      searchTickerValue={searchTickerValue}
      selectedTickerValue={selectedTickerValue}
      onTickerInputChange={handleInputTickerFilterChange}
      onTickerInputClick={handleInputTickerFIlterClick}
      isDropdownOpen={isDropdownOpen}
      isLoading={isLoading}
      searchTickerResultItems={searchTickerResultItems}
      isFromToDisabled={isFromToDisabled}
      isDateRangeDisabled={isDateRangeDisabled}
      maxDate={maxDate}
      onSubmit={handleSubmitFilter}
      onClear={handleClearFilter}
    />
  );

  const pg = usePagination(trades, { defaultPageSize: 5 });
  const {
    page,
    totalCount,
    numResults: num_results,
    setNumResults,
    startOffset: pageStartOffset,
    endOffset: pageEndOffset,
    canGoPrev: backPageEnable,
    canGoNext: nextPageEnable,
  } = pg;
  const [selectedRow, setSelectedRow] = useState([]);
  const [ids, setSelectedTradeIds] = useState([]);
  const [opentradesLoading, setOpenTradesLoading] = useState(false);
  const [closeTradePopup, setCloseTradePopup] = useState(false);
  const [reviewPopup, setReviewPopup] = useState(false);
  const [reviewTradeIds, setReviewTradeIds] = useState([]);
  const [reviewSetupType, setReviewSetupType] = useState("");
  const [reviewFollowedPlan, setReviewFollowedPlan] = useState("");
  const [reviewEmotionalState, setReviewEmotionalState] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewScreenshot, setReviewScreenshot] = useState(null);
  const [trade_date, setTradeDate] = useState("");

  const [trade_type, setTradeType] = useState("");
  const [security_type, setSecurityType] = useState("");
  const [trade_direction, setTradeDirection] = useState("");
  const [ticker_name, setTickerName] = useState("");
  const [expiry, setExpiry] = useState("");
  const [strike, setStrike] = useState("");
  const [buy_value, setBuyValue] = useState("");
  const [units, setUnits] = useState("");
  const [rr, setRR] = useState("");
  const [pnl, setPNL] = useState("");
  const [percent_wl, setPercentWL] = useState("");
  const [comments, setComments] = useState("");

  const rrformat = (val1,val2) => val1 + ":" + val2;
  const [risk, setRisk] = useState("1");
  const [reward, setReward] = useState("1");

  useEffect(() => {
    if(risk > 0 && reward > 0 && rr !== rrformat(risk, reward)){
      setRR(rrformat(risk,reward));
    }
  }, [risk, reward]); 

  function clearFormStates() {
    setTradeType("");
    setSecurityType("");
    setTradeDirection("");
    setTickerName("");
    setSelectedValue("");
    setSearchValue("");
    setTradeDate("");
    setExpiry("");
    setStrike("");
    setBuyValue("");
    setUnits("");
    setRR("");
    setPNL("");
    setPercentWL("");
    setComments("");
    setRisk( "1");
    setReward( "1");
  }


  const handleNextPage = async (e) => {
    if(nextPageEnable){
      setOpenTradesLoading(true);
      const filters = {};
      if(filter_trade_type !== ''){
        filters.trade_type = filter_trade_type;
      }
      if(filter_security_type !== ''){
        filters.security_type = filter_security_type;
      }
      if(filter_trade_direction !== ''){
        filters.trade_direction = filter_trade_direction;
      }
      if(filter_ticker_name !== ''){
        filters.ticker_name = filter_ticker_name;
      }
      if(filter_switch_time !== '' && (filter_from_date === '' && filter_to_date === '')){
        filters.date_range = filter_switch_time;
      }
      if(filter_switch_time === '' && (filter_from_date !== '' || filter_to_date !== '')){
        if (filter_from_date !== '' && filter_to_date !== ''){
          filters.from_date = filter_from_date;
          filters.to_date = filter_to_date;
        }else if (filter_from_date !== '' && filter_to_date === ''){
          filters.from_date = filter_from_date;
          filters.to_date = today;
        }
        else if (filter_from_date === '' && filter_to_date !== ''){
          filters.from_date = "1900-01-01";
          filters.to_date = filter_to_date;
        }
      }
      filters.page = page+1;
      filters.numrows = num_results;
      filters.trade_date = 'NULL';
      await dispatch(getTradesPage({ filters }));  
      setSelectedRow([]);
      setOpenTradesLoading(false);
    }
  }

  const handleBackPage = async (e) => {
    if(backPageEnable){
      setOpenTradesLoading(true);
      const filters = {};
      if(filter_trade_type !== ''){
        filters.trade_type = filter_trade_type;
      }
      if(filter_security_type !== ''){
        filters.security_type = filter_security_type;
      }
      if(filter_trade_direction !== ''){
        filters.trade_direction = filter_trade_direction;
      }
      if(filter_ticker_name !== ''){
        filters.ticker_name = filter_ticker_name;
      }
      if(filter_switch_time !== '' && (filter_from_date === '' && filter_to_date === '')){
        filters.date_range = filter_switch_time;
      }
      if(filter_switch_time === '' && (filter_from_date !== '' || filter_to_date !== '')){
        if (filter_from_date !== '' && filter_to_date !== ''){
          filters.from_date = filter_from_date;
          filters.to_date = filter_to_date;
        }else if (filter_from_date !== '' && filter_to_date === ''){
          filters.from_date = filter_from_date;
          filters.to_date = today;
        }
        else if (filter_from_date === '' && filter_to_date !== ''){
          filters.from_date = "1900-01-01";
          filters.to_date = filter_to_date;
        }
      }
      filters.page = page-1;
      filters.numrows = num_results;
      filters.trade_date = 'NULL';
      await dispatch(getTradesPage({ filters }));  
      setSelectedRow([]);
      setOpenTradesLoading(false);
    }
  }

  const handleChangeNumResults = async (e) => {
    const new_num_results = e.target.value;
    setNumResults(new_num_results);
    setOpenTradesLoading(true);
    const filters = {};
    if(filter_trade_type !== ''){
      filters.trade_type = filter_trade_type;
    }
    if(filter_security_type !== ''){
      filters.security_type = filter_security_type;
    }
    if(filter_trade_direction !== ''){
      filters.trade_direction = filter_trade_direction;
    }
    if(filter_ticker_name !== ''){
      filters.ticker_name = filter_ticker_name;
    }
    if(filter_switch_time !== '' && (filter_from_date === '' && filter_to_date === '')){
      filters.date_range = filter_switch_time;
    }
    if(filter_switch_time === '' && (filter_from_date !== '' || filter_to_date !== '')){
      if (filter_from_date !== '' && filter_to_date !== ''){
        filters.from_date = filter_from_date;
        filters.to_date = filter_to_date;
      }else if (filter_from_date !== '' && filter_to_date === ''){
        filters.from_date = filter_from_date;
        filters.to_date = today;
      }
      else if (filter_from_date === '' && filter_to_date !== ''){
        filters.from_date = "1900-01-01";
        filters.to_date = filter_to_date;
      }
    }
    filters.page = 1;
    filters.numrows = new_num_results;
    filters.trade_date = 'NULL';
    await dispatch(getTradesPage({ filters }));  
    setSelectedRow([]);
    setOpenTradesLoading(false);
  }

  const handleSelectAll = (e) => {
    if (hasTrades && trades.trades.length === 1) {
      setSelectedRow([]);
      setTradeID(trades.trades[0].trade_id);
      setSelectedRow([...selectedRow, 0]);
    }else if (hasTrades && trades.trades.length > 1){
      setSelectedRow([]);
      setTradeID(null);
      setSelectedRow(Array.from({ length: trades.trades.length }, (_, index) => index));
    }
  }

  const handleClearSelected = (e) => {
    setSelectedRow([]);
    setTradeID(null);
  }

  const handleGotoClose = (e) => {
    setSelectedTradeIds(selectedRow.map(index => trades.trades[index].trade_id));
    setAccountValueUpdateTradesEnable(true);
    setCloseTradePopup(true);
  };

  const handleConfirmClose = async (e) => {
    e.preventDefault();
    setSelectedRow([]);
    setOpenTradesLoading(true);
    setCloseTradePopup(false);
    let update_info = {
      trade_type,
      security_type,
      trade_direction,
      ticker_name,
      trade_date,
      expiry,
      strike,
      buy_value,
      units,
      rr,
      pnl,
      percent_wl,
      comments
    }
    await dispatch(
      updateTrades({
        accountValueUpdateTradesEnable,
        ids,
        update_info
      })
    );
    clearFormStates();
    setOpenTradesLoading(false);
    // Show review dialog for the closed trades
    setReviewTradeIds([...ids]);
    setSelectedTradeIds([]);
    setReviewPopup(true);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    for (const tradeId of reviewTradeIds) {
      const reviewResult = await dispatch(
        createTradeReview({
          trade_id: tradeId,
          setup_type: reviewSetupType || null,
          followed_plan: reviewFollowedPlan === "" ? null : reviewFollowedPlan === "true",
          emotional_state: reviewEmotionalState || null,
          notes: reviewNotes || null,
        })
      );
      if (reviewScreenshot && reviewResult.payload && reviewResult.payload.trade_review_id) {
        await dispatch(
          uploadReviewScreenshot({
            trade_review_id: reviewResult.payload.trade_review_id,
            selectedFile: reviewScreenshot,
          })
        );
      }
    }
    setReviewPopup(false);
    clearReviewStates();
    showToast("Trade Review Saved Successfully");
  };

  const handleSkipReview = () => {
    setReviewPopup(false);
    clearReviewStates();
  };

  const clearReviewStates = () => {
    setReviewTradeIds([]);
    setReviewSetupType("");
    setReviewFollowedPlan("");
    setReviewEmotionalState("");
    setReviewNotes("");
    setReviewScreenshot(null);
  };

  const handleCancelClose = (e) => {
    setSelectedRow([]);
    setCloseTradePopup(false);
    setSelectedTradeIds([]);
    clearFormStates();
  };

  useEffect(() => {
    calculatePercent();
  }, [pnl, buy_value, units]); 

  const calculatePercent = () => {
    const pnlFloat=parseFloat(pnl);
    const buyValueFloat=parseFloat(buy_value);
    const unitsFloat=parseFloat(units);
    if (!isNaN(pnlFloat) && !isNaN(buyValueFloat) && buyValueFloat !== 0 && !isNaN(unitsFloat) && unitsFloat !== 0) {
      setPercentWL(((pnlFloat/(buyValueFloat*unitsFloat)*100).toFixed(2)));
    }else{
      setPercentWL("");
    }
  }

  //sorting logic
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const sortedTrades = React.useMemo(() => {
    if(hasTrades) {
      let sortableItems = [...trades.trades];
      if (sortConfig.key !== null) {
        sortableItems.sort((a, b) => {
          const aValue = a[sortConfig.key];
          const bValue = b[sortConfig.key];
  
          // Handle null values
          if (aValue === null) return sortConfig.direction === 'asc' ? -1 : 1;
          if (bValue === null) return sortConfig.direction === 'asc' ? 1 : -1;
  
          if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        });
      }
      return sortableItems;
    }
  }, [trades, sortConfig]);

  const requestSort = key => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? <TriangleUpIcon /> : <TriangleDownIcon />;
    }
    return <IoFilter />;
  };

  const getOpenTrades = () => {
    let content = [];
    content.push(
      <>
      {opentradesLoading ?
        <Stack flex="auto" p="1rem" h="full" w="full">
          <Center><TradingSpinner size="40px" /></Center>
        </Stack>
      :
      <>
      <Stack
        flex="auto"
        w="full"
      >
      <HStack spacing={2} mb={3} flexWrap="wrap">
      <Button size="sm" variant="outline" colorScheme="gray" borderRadius="md" onClick={(e) => handleSelectAll(e.target.value)}>
        Select All
      </Button>
      <Button size="sm" variant="outline" colorScheme="gray" borderRadius="md" onClick={(e) => handleClearSelected(e.target.value)}>
        Clear
      </Button>
      <Button size="sm" colorScheme="blue" borderRadius="md" isDisabled={selectedRow.length <= 0} onClick={e => handleGotoClose(e)}>
        Close
      </Button>
      </HStack>
      <AlertDialog
        motionPreset='slideInBottom'
        isOpen={closeTradePopup}
        leastDestructiveRef={cancelRef}
        onClose={e => handleCancelClose(e)}
        isCentered={true}
        size='sm'
        closeOnOverlayClick={true}
      >
        <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize='lg' fontWeight='bold' pb={1}>
            Close Trade
          </AlertDialogHeader>

          <AlertDialogBody pt={1}>
            <VStack spacing={2} align="stretch">
            <FormControl>
              <FormHelperText mb={1} ml={1}>
                Closure Date
              </FormHelperText>
              <Input
                  value={trade_date}
                  type="date"
                  max={maxDate}
                  min="1900-01-01"
                  size="sm"
                  onChange={(e) => setTradeDate(e.target.value)}
              />
            </FormControl>
            <FormControl>
                  <FormHelperText mb={1} ml={1}>
                    Average Cost *
                  </FormHelperText>
                  <Input
                    value={buy_value}
                    type="name"
                    size="sm"
                    onChange={(e) => setBuyValue(e.target.value)}
                  />
              </FormControl>
              <FormControl>
                  <FormHelperText mb={1} ml={1}>
                    # of Units *
                  </FormHelperText>
                  <Input
                    value={units}
                    type="name"
                    size="sm"
                    onChange={(e) => setUnits(e.target.value)}
                  />
              </FormControl>
              <FormControl>
                  <FormHelperText mb={1} ml={1}>
                    PNL *
                  </FormHelperText>
                  <Input
                    value={pnl}
                    type="name"
                    size="sm"
                    onChange={(e) => setPNL(e.target.value)}
                  />
              </FormControl>
              <FormControl>
                  <FormHelperText mb={1} ml={1}>
                    % Win or Loss *
                  </FormHelperText>
                  <Input
                    value={percent_wl}
                    type="name"
                    readOnly
                    placeholder={"0.00"}
                    size="sm"
                    value={percent_wl}
                  />
              </FormControl>
              <AccountValueSyncToggle
                isChecked={accountValueUpdateTradesEnable}
                onChange={(e) => handleCheckboxChange(e, "UpdateTrades")}
                actionLabel="closing these trades"
                pl={2}
                pt={1}
              />
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter paddingTop={4}>
            <Button ref={cancelRef} minWidth='50px' onClick={e => handleCancelClose(e)}>
              Cancel
            </Button>
            <Button colorScheme='blue' minWidth='50px' isDisabled={!trade_date} onClick={e => handleConfirmClose(e)} ml={3}>
              Submit
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <AlertDialog
        motionPreset='slideInBottom'
        isOpen={reviewPopup}
        leastDestructiveRef={cancelRef}
        onClose={handleSkipReview}
        isCentered={true}
        size='md'
        closeOnOverlayClick={true}
      >
        <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize='lg' fontWeight='bold'>
            Post-Trade Review
          </AlertDialogHeader>

          <AlertDialogBody>
            <FormControl mb={3}>
              <FormHelperText mb={2} ml={1}>
                Setup Type
              </FormHelperText>
              <Input
                placeholder="e.g. Breakout, Reversal, Momentum"
                value={reviewSetupType}
                onChange={(e) => setReviewSetupType(e.target.value)}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormHelperText mb={2} ml={1}>
                Did you follow your plan?
              </FormHelperText>
              <Select
                placeholder="-- Select --"
                value={reviewFollowedPlan}
                onChange={(e) => setReviewFollowedPlan(e.target.value)}
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Select>
            </FormControl>
            <FormControl mb={3}>
              <FormHelperText mb={2} ml={1}>
                Emotional State
              </FormHelperText>
              <Select
                placeholder="-- Select --"
                value={reviewEmotionalState}
                onChange={(e) => setReviewEmotionalState(e.target.value)}
              >
                <option value="calm">Calm</option>
                <option value="anxious">Anxious</option>
                <option value="excited">Excited</option>
                <option value="fearful">Fearful</option>
                <option value="revenge">Revenge</option>
              </Select>
            </FormControl>
            <FormControl mb={3}>
              <FormHelperText mb={2} ml={1}>
                What would you do differently?
              </FormHelperText>
              <Textarea
                placeholder="Notes on the trade..."
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                rows={3}
              />
            </FormControl>
            <FormControl>
              <FormHelperText mb={2} ml={1}>
                Chart Screenshot (optional)
              </FormHelperText>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setReviewScreenshot(e.target.files[0] || null)}
                p={1}
              />
            </FormControl>
          </AlertDialogBody>
          <AlertDialogFooter paddingTop={6}>
            <Button ref={cancelRef} minWidth='50px' onClick={handleSkipReview}>
              Skip
            </Button>
            <Button colorScheme='blue' minWidth='50px' onClick={handleSubmitReview} ml={3}>
              Save Review
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      </Stack>
      {hasTrades && sortedTrades.length > 0 ?
      <>
      <TableContainer overflowY="auto" overflowX="auto" rounded="lg" h="80%" maxHeight="400px" borderWidth="2px" borderColor={colorMode === 'light' ? "gray.200" : "gray.600"}>
        <Table size='sm' variant='simple' colorScheme='gray'>
          <Thead position="sticky" top={0} bgColor={colorMode === 'light' ? "gray.200" : "gray.700"} zIndex={2}>
            <Tr>
              <Th>
                <HStack spacing={1}>
                  <Text>Trade Type</Text>
                  <IconButton aria-label="Sort by Trade Type" icon={getSortIcon('trade_type')} onClick={() => requestSort('trade_type')} size="xs" variant="ghost" />
                </HStack>
              </Th>
              <Th>
                <HStack spacing={1}>
                  <Text>Security Type</Text>
                  <IconButton aria-label="Sort by Security Type" icon={getSortIcon('security_type')} onClick={() => requestSort('security_type')} size="xs" variant="ghost" />
                </HStack>
              </Th>
              <Th>
                <HStack spacing={1}>
                  <Text>Direction</Text>
                  <IconButton aria-label="Sort by Direction" icon={getSortIcon('trade_direction')} onClick={() => requestSort('trade_direction')} size="xs" variant="ghost" />
                </HStack>
              </Th>
              <Th>
                <HStack spacing={1}>
                  <Text>Ticker</Text>
                  <IconButton aria-label="Sort by Ticker" icon={getSortIcon('ticker_name')} onClick={() => requestSort('ticker_name')} size="xs" variant="ghost" />
                </HStack>
              </Th>
              <Th>
                <HStack spacing={1}>
                  <Text># of Units</Text>
                  <IconButton aria-label="Sort by # of Units" icon={getSortIcon('units')} onClick={() => requestSort('units')} size="xs" variant="ghost" />
                </HStack>
              </Th>
              <Th>
                <HStack spacing={1}>
                  <Text>Avg Price</Text>
                  <IconButton aria-label="Sort by Avg Price" icon={getSortIcon('buy_value')} onClick={() => requestSort('buy_value')} size="xs" variant="ghost" />
                </HStack>
              </Th>
            </Tr>
          </Thead>
              <Tbody zIndex={1}>
                {sortedTrades.map((trade, index) => (
                  <Tr
                  onClick={async () => {
                    if (selectedRow.includes(index)) {
                      const filteredSelectedRow = selectedRow.filter((row) => row !== index);
                      await setSelectedRow(filteredSelectedRow);
                      if (selectedRow.length === 2){
                        const indexofIndex = selectedRow.indexOf(index);
                        if(indexofIndex === 0){
                          setTradeID(trades.trades[selectedRow[1]].trade_id);
                        } else{
                          setTradeID(trades.trades[selectedRow[0]].trade_id);
                        }
                      } else if (selectedRow.length === 1) {
                        setTradeID(null);
                      }
                    } else if (!selectedRow.includes(index) && selectedRow.length === 0) {
                      setSelectedRow([...selectedRow, index]); 
                      setTradeID(trade.trade_id);
                    } else if (!selectedRow.includes(index) && selectedRow.length >= 1) {
                      setSelectedRow([...selectedRow, index]); 
                      setTradeID(null);
                    } 
                  }}
                  cursor="pointer"
                  bgColor={selectedRow.includes(index) ? (colorMode === 'light' ? 'gray.200' : 'gray.900') : (colorMode === 'light' ? 'white' : 'gray.700')}
                  _hover={!selectedRow.includes(index) ? { bg: colorMode === 'light' ? 'gray.50' : 'gray.600' } : {}}
                  >
                    <Td>{trade.trade_type}</Td>
                    <Td>{trade.security_type}</Td>
                    <Td>{trade.trade_direction}</Td>
                    <Td>{trade.ticker_name}</Td>
                    <Td isNumeric>{trade.units}</Td>
                    <Td isNumeric>{trade.buy_value}</Td>
                  </Tr>
                ))}
              </Tbody>
        </Table>
      </TableContainer>
      <HStack spacing={1} pt={2} justifyContent="flex-end" flexWrap="wrap">
        <HStack spacing={1}>
          <Text fontSize="xs" fontWeight="semibold">Rows:</Text>
          <Select maxW={70} variant="filled" size="xs" value={num_results} onChange={(e) => handleChangeNumResults(e)}>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
          </Select>
        </HStack>
        <HStack spacing={0}>
          <IconButton aria-label="Previous page" icon={<ChevronLeftIcon />} size="xs" minW="20px" h="20px" variant="ghost" borderRadius="full" isDisabled={!backPageEnable} onClick={e => handleBackPage(e)} />
          <IconButton aria-label="Next page" icon={<ChevronRightIcon />} size="xs" minW="20px" h="20px" variant="ghost" borderRadius="full" isDisabled={!nextPageEnable} onClick={e => handleNextPage(e)} />
          <Text fontSize="xs" fontWeight="semibold">{pageStartOffset}-{pageEndOffset} of {totalCount}</Text>
        </HStack>
      </HStack>
      </>
      :
      <Center py={8} rounded="lg" borderWidth="2px" borderColor={colorMode === 'light' ? "gray.200" : "gray.600"}>
        <Text fontSize="sm" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
          No open trades.
        </Text>
      </Center>
      }
      </>
      }
      </>
    );
    return content;
  };

  const handleSubmitFilter = async (e) => {
    e.preventDefault();
    onClose();
    const filters = {};
    if(filter_trade_type !== ''){
      filters.trade_type = filter_trade_type;
    }
    if(filter_security_type !== ''){
      filters.security_type = filter_security_type;
    }
    if(filter_trade_direction !== ''){
      filters.trade_direction = filter_trade_direction;
    }
    if(filter_ticker_name !== ''){
      filters.ticker_name = filter_ticker_name;
    }
    if(filter_switch_time !== '' && (filter_from_date === '' && filter_to_date === '')){
      filters.date_range = filter_switch_time;
    }
    if(filter_switch_time === '' && (filter_from_date !== '' || filter_to_date !== '')){
      if (filter_from_date !== '' && filter_to_date !== ''){
        filters.from_date = filter_from_date;
        filters.to_date = filter_to_date;
      }else if (filter_from_date !== '' && filter_to_date === ''){
        filters.from_date = filter_from_date;
        filters.to_date = today;
      }
      else if (filter_from_date === '' && filter_to_date !== ''){
        filters.from_date = "1900-01-01";
        filters.to_date = filter_to_date;
      }
    }
    await dispatch(
      getTradesStatsFiltered({
        filters
      })
    );
    handleFilterChange(filtersToQueryString(filters));
    window.localStorage.setItem('HomeFilters', JSON.stringify(filters));
    filters.page = 1;
    filters.numrows = 5;
    filters.trade_date = 'NULL';
    await dispatch(getTradesPage({ filters }));
    setSelectedRow([]);
    setFilters(filters);
    //setToggleFilter(!toggleFilter);
  }

  const handleClearFilter = async (e) => {
    e.preventDefault();
    onClose();
    setFilterTradeType('');
    setFilterSecurityType('');
    setFilterTradeDirection('');
    setFilterTickerName('');
    setFilterSwitchDate('');
    setFilterFromDate('');
    setFilterToDate('');
    setSearchTickerValue('');
    setSelectedTickerValue('');
    await dispatch(getTradesStats());
    handleFilterChange(filtersToQueryString({}));
    window.localStorage.removeItem('HomeFilters');
    const filters = {};
    filters.page = 1;
    filters.numrows = 5;
    filters.trade_date = 'NULL';
    await dispatch(getTradesPage({ filters }));
    setFilters({});
    setSelectedRow([]);
    //setToggleFilter(!toggleFilter);
  }

  useEffect(() => {
    evaluatePreferences();
  }, [preferences]); 

  const evaluatePreferences = () => {
    if(preferences && preferences.account_value_optin === 1){
      setFeatureFlag(true);
    }else{
      setFeatureFlag(false);
    }
  }
  const handleLogTrade = (e) => {
    navigate("/logTrade");
  }

  // grabbing current date to set a max to the birthday input
  const currentDate = new Date();
  let [month, day, year] = currentDate.toLocaleDateString().split("/");
  // input max field must have 08 instead of 8
  month = month.length === 2 ? month : "0" + month;
  day = day.length === 2 ? day : "0" + day;
  const maxDate = year + "-" + month + "-" + day;        

  return (
      
      <Flex           
        flexDirection="column"
        height="100vh"
        backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}
      >
       
        
        <Box flex="auto" overflowX="auto" overflowY="auto">
          <AnimatePresence mode="wait">
          {(authLoading || !initialDataLoaded) && !optInAlertDialog && !avtimeloading && !opentradesLoading ? (
            <motion.div key="skeleton" initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              <VStack flex="auto" p={4} spacing={4} w="full" align="stretch">
                <HomeSkeleton />
              </VStack>
            </motion.div>
          ) : (
            <motion.div key="content" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
            <VStack flex="auto" p={4} spacing={4} w="full" overflowX="auto" align="stretch">
            {hasStats && hasPreferences && hasAVs && hasTrades ? (
            <>
            {/* Filter Bar */}
            <Box mb={-2}>{getFilterComponent()}</Box>

            {/* ROW 1: Hero Stats */}
            <HeroStatsRow
              stats={stats.stats}
              preferences={preferences}
              todayAccountValue={todayAccountValue}
              today={today}
              featureFlag={featureFlag}
              format={format}
              formatter={formatter}
              percent={percent}
              pnlValue={pnlValue}
              colorChange={colorChange}
              onEditAccountValue={(e) => handleOptInButton(e)}
              planAdherence={planAdherence}
            />

            {/* Collapsible Sections: Detailed Stats (closed), Watchlist (closed), Open Trades (only rendered when present, open by default), Charts (open) */}
            <Accordion allowMultiple defaultIndex={trades?.trades?.length > 0 ? [2, 3] : [2]}>
              {/* Detailed Statistics */}
              <AccordionItem border="none" mb={3}>
                <AccordionButton bg={colorMode === 'light' ? 'white' : 'gray.700'} rounded="lg" boxShadow="sm" _hover={{ bg: colorMode === 'light' ? 'gray.50' : 'gray.600' }}>
                  <Box flex="1" textAlign="left">
                    <Heading className="page-heading" fontSize="md" color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>Detailed Statistics</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4} pt={4} px={0}>
                  <DetailedStatsGrid
                    stats={stats.stats}
                    preferences={preferences}
                    formatter={formatter}
                    pnlValue={pnlValue}
                    colorChange={colorChange}
                    planAdherence={planAdherence}
                    riskReward={riskReward}
                    accountValueMetrics={accountValueMetrics}
                    journalStreak={journalStreak}
                  />
                </AccordionPanel>
              </AccordionItem>

              {/* Watchlist (TRADE-973) */}
              <AccordionItem border="none" mb={3}>
                <AccordionButton bg={colorMode === 'light' ? 'white' : 'gray.700'} rounded="lg" boxShadow="sm" _hover={{ bg: colorMode === 'light' ? 'gray.50' : 'gray.600' }}>
                  <Box flex="1" textAlign="left">
                    <Heading className="page-heading" fontSize="md" color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>Watchlist</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4} pt={4} px={0}>
                  <HomeWatchlistCard />
                </AccordionPanel>
              </AccordionItem>

              {/* Open Trades — only render if there are open trades */}
              {trades?.trades?.length > 0 && (
                <AccordionItem border="none" mb={3}>
                  <AccordionButton bg={colorMode === 'light' ? 'white' : 'gray.700'} rounded="lg" boxShadow="sm" _hover={{ bg: colorMode === 'light' ? 'gray.50' : 'gray.600' }}>
                    <Box flex="1" textAlign="left">
                      <Heading className="page-heading" fontSize="md" color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>Open Trades</Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} pt={4} px={0}>
                    <Box bg={colorMode === 'light' ? 'white' : 'gray.700'} rounded="lg" p={4} boxShadow="sm">
                      {getOpenTrades()}
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              )}

              {/* Charts */}
              <AccordionItem border="none">
                <AccordionButton bg={colorMode === 'light' ? 'white' : 'gray.700'} rounded="lg" boxShadow="sm" _hover={{ bg: colorMode === 'light' ? 'gray.50' : 'gray.600' }}>
                  <Box flex="1" textAlign="left">
                    <Heading className="page-heading" fontSize="md" color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>Charts</Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4} pt={4} px={0}>
                  {/* Pie / Donut Charts — individual cards */}
                  <TradeBreakdownCharts
                    stats={stats.stats}
                    makePieOptions={makePieOptions}
                  />

                  {/* Account Value — full-width area chart card */}
                  <AccountValueChartCard
                    accountValues={accountValues}
                    preferences={preferences}
                    featureFlag={featureFlag}
                    filter_av_time={filter_av_time}
                    onFilterAvTimeChange={setFilterAvDate}
                    avtimeloading={avtimeloading}
                    format={format}
                    setDateFormat={setDateFormat}
                    makeAreaOptions={makeAreaOptions}
                    onOptInClick={(e) => handleOptInButton(e)}
                    accountValueMetrics={accountValueMetrics}
                  />
                  <AccountValueOptInDialog
                    isOpen={optInAlertDialog}
                    cancelRef={cancelRef}
                    authLoading={authLoading}
                    preferences={preferences}
                    hasPreferences={hasPreferences}
                    accountvalue={accountvalue}
                    onAccountValueChange={setAccountvalue}
                    format={format}
                    parse={parse}
                    onCancel={(e) => handleCancelOptIn(e)}
                    onConfirm={(e) => handleConfirmOptIn(e)}
                  />
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
            </>
            ) : (
              <Center flex="1">
                <VStack spacing={4} mt={10}>
                  <Lottie animationData={animationData} loop={true} />
                  <Text className='no-trade-text'>No Trade Data To Display, Please Add Trades</Text>
                  <Button size="sm" width='100px' backgroundColor='gray.300' color={colorMode === 'light' ? "none" : "gray.800"} borderRadius="md"
                    boxShadow={colorMode === 'light' ? '2px 4px 4px rgba(0,0,0,0.2)' : '2px 4px 4px rgba(255,255,255,0.2)'}
                    onClick={(e) => handleLogTrade(e.target.value)}>
                    + Add Trade
                  </Button>
                </VStack>
              </Center>
            )}
            </VStack>
            </motion.div>
          )}
          </AnimatePresence>
        </Box>
      </Flex>
  )
}

